// frame: yDAqU @ ui/dogdisk-agent.pen (设置主页)
//
// 2026-07-01 精简:只保留「账户安全」段(修改密码 + 登录历史 + 系统信息附录)。
// 通知偏好 / 资料(手机换绑·USDT·银行卡) / 关于(协议·隐私·客服·注销) / 两步验证
// 全部删除;「前往个人资料编辑」按钮随 /profile 页删除一并移除。
//
// 数据源:GET /rest/v1/v_agent_me_profile (security_invoker;auth.uid() WHERE) —— 仅取
// agent_level / parent_agent_id / agent_since 展示系统信息。
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getSupabase } from '@dogdisk/shared'
import { History, KeyRound } from 'lucide-react'
import { AgentLayout } from '../components/agent-layout'
import { ChangePasswordModal } from '../modals/change-password'

type AgentMeProfile = {
  user_id: string
  agent_level: number | null
  parent_agent_id: string | null
  agent_since: string | null
}

function useAgentMeProfile() {
  return useQuery({
    queryKey: ['agent', 'me', 'profile'] as const,
    queryFn: async (): Promise<AgentMeProfile | null> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_agent_me_profile')
        .select('user_id, agent_level, parent_agent_id, agent_since')
        .maybeSingle()
      if (error) throw error
      return (data as AgentMeProfile | null) ?? null
    },
    staleTime: 30_000,
  })
}

export function AgentSettingsPage() {
  const navigate = useNavigate()
  const { data: me } = useAgentMeProfile()
  const [pwOpen, setPwOpen] = useState(false)

  return (
    <AgentLayout topbarTitle="代理 · 设置">
      <div className="mx-auto flex w-[800px] flex-col gap-0 px-6 py-8" data-testid="yDAqU">
        {/* sec1 账户安全(精简后唯一段) */}
        <Section title="账户安全" first last>
          <Row
            icon={<KeyRound size={18} />}
            label="修改密码"
            right={
              <RightBtn onClick={() => setPwOpen(true)} testid="settings-change-pw">
                修改 →
              </RightBtn>
            }
          />
          <Row
            icon={<History size={18} />}
            label="登录历史"
            right={
              <RightBtn onClick={() => navigate('/login-history')} testid="settings-login-history">
                查看 →
              </RightBtn>
            }
          />
          {/* 系统信息(level / parent / since)— sec1 末尾附录区 */}
          {me?.agent_level != null && (
            <div
              className="mt-1 flex items-center justify-between border-t border-gray-100 py-3.5 text-xs text-gray-500"
              data-testid="yDAqU-sys-info"
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-400">系统信息</span>
              </div>
              <div className="flex items-center gap-3 font-mono">
                <span>L{me.agent_level}</span>
                {me.parent_agent_id ? (
                  <span title={me.parent_agent_id}>parent: {me.parent_agent_id.slice(0, 6)}…</span>
                ) : (
                  <span>无 parent</span>
                )}
                {me.agent_since && <span>since {me.agent_since.slice(0, 10)}</span>}
              </div>
            </div>
          )}
        </Section>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </AgentLayout>
  )
}

// ─── ui helpers ───

function Section({
  title,
  children,
  first,
  last,
}: {
  title: string
  children: React.ReactNode
  first?: boolean
  last?: boolean
}) {
  const radius = first && last ? 'rounded-xl' : first ? 'rounded-t-xl' : last ? 'rounded-b-xl' : ''
  return (
    <section
      className={`flex flex-col border-x border-b border-gray-200 bg-white px-7 py-6 ${
        first ? 'border-t' : ''
      } ${radius}`}
    >
      <h2 className="pb-4 text-base font-bold text-gray-900">{title}</h2>
      <div className="flex flex-col">{children}</div>
    </section>
  )
}

function Row({
  icon,
  label,
  right,
}: {
  icon: React.ReactNode
  label: string
  right: React.ReactNode
}) {
  return (
    <div
      className="flex items-center justify-between border-t border-gray-100 py-3.5 first:border-t-0"
      data-row-label={label}
    >
      <div className="flex items-center gap-3 text-gray-700">
        <span className="text-gray-500">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <div>{right}</div>
    </div>
  )
}

function RightBtn({
  onClick,
  children,
  testid,
}: {
  onClick: () => void
  children: React.ReactNode
  testid?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm font-medium text-amber-500 hover:text-amber-600"
      {...(testid ? { 'data-testid': testid } : {})}
    >
      {children}
    </button>
  )
}
